/* CS-81: sticky add-to-cart visibility and sync (see snippets/sticky-atc.liquid).
 *
 * CS-87 (founder 18 Aug second ruling): the bar shows WHENEVER the on-page Add to bag button is
 * off screen, INCLUDING at page load with the buy box below the fold, so the product can be
 * added the moment the page opens. It hides only while the real buy box is on screen. The first
 * W5 build deliberately held the bar back until the buy box had been met and scrolled past; the
 * founder reversed that. What stays from W5: the 64px hysteresis band on BOTH edges of the
 * viewport (a single edge flickers while a half-visible button sits exactly on it): off screen
 * past the band = show; back in view = hide; inside the band the bar keeps its state. While
 * visible, `sticky-atc-visible` sits on <html>; the CS-81 CSS keys BOTH the bar's transform and
 * the `--sticky-bar-h` custom property off that class, so the contact launcher's bottom offset
 * follows automatically (the corner contract).
 *
 * Price and variant name stay honest by listening to the theme's own
 * `cascade:product:variantchange`, which product.js already dispatches with the full variant.
 * The quantity readout (W5) mirrors the section's own quantity input: on its input and change
 * events, and again every time the bar shows or the variant changes, which also catches the
 * writers that fire no event at all (the Avada Order Limit app seeding the MOQ, the Foxkit app
 * writing a chosen tier's quantity: both are script writes).
 */
(() => {
  'use strict';

  function wire(bar) {
    if (bar.dataset.csStickyWired) return;
    bar.dataset.csStickyWired = '1';

    const formId = bar.querySelector('[form]') && bar.querySelector('[form]').getAttribute('form');
    const form = formId && document.getElementById(formId);
    // The on-page submit button is the sentinel: the bar exists only while it is off screen.
    const sentinel = form && form.querySelector('[type="submit"], button[name="add"]');
    if (!sentinel) return;

    bar.hidden = false; // visibility is class-driven from here; [hidden] was only the no-JS state

    // ---- W5 quantity readout ----------------------------------------------------------------
    // The theme renders the quantity input OUTSIDE the form, linked by the form attribute.
    const qtyInput = document.querySelector('input[name="quantity"][form="' + formId + '"]');
    const qtyOut = bar.querySelector('[data-psab-qty]');
    function refreshQty() {
      if (!qtyInput || !qtyOut) return;
      const value = parseInt(qtyInput.value, 10);
      const show = !Number.isNaN(value) && value > 0;
      if (show) {
        qtyOut.textContent = '× ' + value;
        qtyOut.setAttribute('aria-label', 'Quantity ' + value);
      }
      qtyOut.hidden = !show;
    }
    if (qtyInput) {
      qtyInput.addEventListener('input', refreshQty);
      qtyInput.addEventListener('change', refreshQty);
    }

    // ---- CS-87 show/hide with a hysteresis band ---------------------------------------------
    let shown = false;
    let nearVisible = true; // the real viewport edge (hide the moment the button returns)
    let farGone = false; // beyond the 64px band on either edge (show, including at page load)
    function apply() {
      const next = farGone ? true : nearVisible ? false : shown;
      if (next === shown) return;
      shown = next;
      if (shown) refreshQty(); // catch the event-less script writers before the bar is seen
      document.documentElement.classList.toggle('sticky-atc-visible', shown);
    }
    const nearIo = new IntersectionObserver(
      (entries) => {
        nearVisible = entries[0].isIntersecting;
        apply();
      },
      { threshold: 0 }
    );
    const farIo = new IntersectionObserver(
      (entries) => {
        // Off screen in EITHER direction shows the bar (the founder's at-load ruling); the
        // symmetric 64px band keeps both boundaries flicker-free.
        farGone = !entries[0].isIntersecting;
        apply();
      },
      { threshold: 0, rootMargin: '64px 0px 64px 0px' }
    );
    nearIo.observe(sentinel);
    farIo.observe(sentinel);

    // ---- variant + tier sync ----------------------------------------------------------------
    const price = bar.querySelector('[data-psab-price]');
    const variantName = bar.querySelector('[data-psab-variant]');
    const symbol = bar.dataset.currencySymbol || '';
    // Founder 19 Aug fourth wave: the bar shows the TIER price when a tier is live (it was
    // stuck on the variant price). The widget owns the maths and broadcasts cs:tiers:state;
    // the bar just paints: discounted price with the listed price struck beside it, or the
    // plain variant price when no tier is active.
    let variantPaise = null;
    let tierState = { active: false, unitPaise: null };
    function formatRupees(paise) {
      const rupees = paise / 100;
      return symbol + (rupees % 1 === 0 ? rupees.toLocaleString('en-IN') : rupees.toLocaleString('en-IN', { minimumFractionDigits: 2 }));
    }
    // ROUND-9c (founder 20 Aug): the bar's price STUCK on the deal figure when the
    // quantity dropped back below the lowest tier, because the no-tier branch only
    // painted from variantPaise, which stays null until a variant change fires. The
    // server-rendered price text is the honest resting value: captured at wire time
    // and restored whenever no tier is active and no variant price is known.
    const initialPriceText = price ? price.textContent : '';
    function paintPrice() {
      if (!price) return;
      if (tierState.active && typeof tierState.unitPaise === 'number') {
        const base = typeof variantPaise === 'number' ? variantPaise : tierState.basePaise;
        price.innerHTML = '';
        price.append(formatRupees(tierState.unitPaise) + ' ');
        const struck = document.createElement('s');
        struck.className = 'psab__struck';
        struck.textContent = formatRupees(base);
        price.append(struck);
      } else if (typeof variantPaise === 'number') {
        price.textContent = formatRupees(variantPaise);
      } else {
        price.textContent = initialPriceText;
      }
    }
    document.body.addEventListener('cs:tiers:state', (e) => {
      tierState = e.detail || { active: false };
      paintPrice();
    });
    document.body.addEventListener('cascade:product:variantchange', (e) => {
      const v = e.detail && e.detail.variant;
      if (!v) return;
      if (typeof v.price === 'number') { variantPaise = v.price; paintPrice(); }
      if (variantName && v.title) variantName.textContent = v.title;
      refreshQty(); // an Avada per-variant rule can move the minimum with the variant
    });

    // The ROUND-9c payment-button MIRROR lived here and was REMOVED on 20 Aug 2026 with the
    // Avada app. It copied whatever Avada decided onto Add to Cart and this bar, because the
    // app disabled only Buy It Now. The native guard (snippets/moq-guard.liquid) now disables
    // all three buy controls itself from our own minimum, so there is nothing left to mirror.
  }

  function init() {
    document.querySelectorAll('[data-cs-sticky-atc]').forEach(wire);
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
  document.addEventListener('shopify:section:load', init);
})();
